// The words of the Sign-in methods page and each method's screen (design
// edxr-workspace/.superpowers/signin-methods/design.md §5), in one place so
// the list and the screen say the same thing.
import { agoText, dateTimeText } from '../../../../shared/date/local-time'
import { formatCount } from '../../../../shared/sso-workspace/format'

// Auth's refusals the pages read by code (every one also carries a sentence
// in `message`, which is what is shown).
export const CODE_MIGRATION_PENDING = 'signin_method.migration_pending'
export const CODE_UNKNOWN = 'signin_method.unknown'
export const CODE_CHECK_FAILED = 'signin_method.check_failed'

export const codeOf = err => (err && err.data && err.data.code) || null

// The three states (auth's signin_methods.state) and a kind with no method
// yet. tone is the Pill's. "Temporarily unavailable" is the emergency
// switch: the button stays on the sign-in pages, greyed out with its notice
// (Microsoft's app gallery listing needs it visible).
export const STATE_ORDER = ['on', 'unavailable', 'off']

export const STATE_VIEW = {
  on: {
    label: 'On',
    tone: 'ok',
    sentence: 'Its button is on every sign-in page, and people sign in with it.'
  },
  unavailable: {
    label: 'Temporarily unavailable',
    tone: 'warn',
    sentence: 'Its button stays on every sign-in page, greyed out with its notice, and nobody can sign in with it. Use this during an incident.'
  },
  off: {
    label: 'Off',
    tone: null,
    sentence: 'Its button is on no sign-in page, and nobody can sign in with it.'
  }
}

export const NOT_SET_UP_VIEW = {
  label: 'Not set up',
  tone: null,
  sentence: "It isn't set up here yet. Setting it up adds it switched off, last in the order; nothing changes on the sign-in pages until it's switched on."
}

export const stateView = method => (method && method.setUp && STATE_VIEW[method.state]) || NOT_SET_UP_VIEW

// While edxr-cli's signin_methods migration hasn't run in this environment
// auth answers the list with available: false and every other call 409
// signin_method.migration_pending; the sign-in pages keep today's list.
export const migrationPendingText = migration =>
  `Sign-in methods need a database update that hasn't run here yet${migration ? ` (${migration})` : ''}. Until it runs, the sign-in pages keep offering what they offer today, and nothing here can be changed.`

// What people see: clients write "Sign in with <label>".
export const buttonText = label => `Sign in with ${label}`

// The warning before switching off a kind whose button must stay visible
// (entryPointRequiredBy: Microsoft's app gallery listing); auth's 409
// signin_method.entry_point_required says the same.
export const entryPointWarning = (requiredBy, label) =>
  `${requiredBy} requires a visible "${buttonText(label)}" while we're listed. Switching it off removes the button from every sign-in page. To keep it visible during an incident, choose "Temporarily unavailable".`

// The confirmation before a method that is on stops being on.
export const leaveOnConfirm = (next, label) =>
  next === 'unavailable'
    ? {
      title: `Make ${label} temporarily unavailable?`,
      body: `"${buttonText(label)}" stays on every sign-in page, greyed out with its notice, and nobody can sign in with it until it's on again. It takes effect when you save.`,
      confirmText: 'Mark unavailable'
    }
    : {
      title: `Switch off ${label}?`,
      body: `"${buttonText(label)}" leaves every sign-in page, and nobody can sign in with it until it's on again. People who use it sign in with their email instead. It takes effect when you save.`,
      confirmText: 'Switch off'
    }

// Auth's limits (signin_methods: label 1-40, notice 280, staff note 2000).
export const MAX_LABEL = 40
export const MAX_NOTICE = 280
export const MAX_STAFF_NOTE = 2000

const length = text => [...String(text || '')].length

// What is wrong with a label, as auth refuses it (400 signin_method.label),
// or null: empty, too long, or starting with "Sign in" (any case, "Sign-in",
// "Signin"), which every client adds itself.
export const labelProblem = (label, defaultLabel) => {
  const text = String(label || '').trim()
  if (!text) return 'Give the button a label.'
  if (length(text) > MAX_LABEL) return `The label is at most ${MAX_LABEL} characters.`
  if (/^sign(\s|-)?in/i.test(text)) return `Leave out "Sign in with": every sign-in page adds it. Type only the name, such as "${defaultLabel || 'Microsoft'}".`
  return null
}

export const lengthProblem = (text, max, what) => (length(String(text || '').trim()) > max ? `The ${what} is at most ${formatCount(max)} characters.` : null)

export const charactersLeft = (text, max) => max - length(text)

// A last failure's reason (auth records IdP, exchange, token and credential
// errors: callback-error.go's reasons and exchange_failed), with the
// method's name.
const FAILURE_REASONS = {
  idp_error: label => `${label} returned an error`,
  not_enrolled: label => `${label} said the account isn't enrolled`,
  credential_expired: label => `${label} refused EducationXR's credential`,
  exchange_failed: label => `Finishing the sign-in with ${label} failed`
}

export const failureReasonText = (reason, label) => {
  const name = label || 'The identity provider'
  if (FAILURE_REASONS[reason]) return FAILURE_REASONS[reason](name)
  return reason ? `${name}: ${String(reason).replace(/_/g, ' ')}` : 'Reason not recorded'
}

// The kind's check (POST /signin-method/:kind/check): a pill per status;
// the server writes the summary and the fix.
const CHECK_VIEW = {
  ok: { tone: 'ok', label: 'Passed' },
  problem: { tone: 'bad', label: 'Problem' },
  not_checked: { tone: null, label: 'Not checked' },
  unreachable: { tone: 'warn', label: "Couldn't reach" },
  inconclusive: { tone: 'warn', label: "Couldn't tell" }
}

export const checkView = check => (check && CHECK_VIEW[check.status]) || CHECK_VIEW.not_checked

// The list's health cells: when it last signed someone in, and how often
// lately; when it last failed, and why. '' when auth sent no health (a kind
// not set up).
export const lastSignInText = (health, now) => {
  if (!health) return ''
  return health.lastSignInAt ? agoText(health.lastSignInAt, { now }) : 'No sign-ins yet'
}

export const signInCountText = health => {
  if (!health || health.signIns7d === undefined || health.signIns7d === null) return ''
  return `${formatCount(health.signIns7d)} in the last 7 days`
}

export const lastFailureText = (health, now) => {
  if (!health) return ''
  return health.lastFailureAt ? agoText(health.lastFailureAt, { now }) : 'None recorded'
}

// The exact local time behind a relative one, for its hover.
export const exactTime = value => (value ? dateTimeText(value) : undefined)

// A configuration value that is an instant (the certificate's expiry):
// shown in the viewer's own time, never the raw UTC string.
export const isInstant = value => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) && Number.isFinite(Date.parse(value))
